import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

// Default expense adjustments structure
const defaultExpenseAdjustments = [
  {
    enabled: false,
    yearsAfterRetirement: 5,
    type: 'reduce',
    percentage: 10,
    reason: 'Kids independent',
  },
  {
    enabled: false,
    yearsAfterRetirement: 15,
    type: 'reduce',
    percentage: 15,
    reason: 'Simpler lifestyle',
  },
  {
    enabled: false,
    yearsAfterRetirement: 20,
    type: 'increase',
    percentage: 10,
    reason: 'Healthcare costs',
  },
];

// Default values - zeros for fresh start
const defaultUserData = {
  comprehensive: {
    currentAge: 0,
    retirementAge: 0,
    lifeExpectancy: 0,
    monthlyExpenses: 0,
    yearlyExpenses: 0,
    expenseType: 'monthly',
    currentCorpus: 0,
    inflationRate: 4.5,
    preRetirementReturn: 12,
    expectedReturn: 10,
    safeWithdrawalRate: 5,
  },
  expenseAdjustments: defaultExpenseAdjustments,
  calculatedFreedomNumber: 0,
  goals: {
    education: { amount: 0, modified: false },
    marriage: { amount: 0, modified: false },
    loanClosure: { amount: 0, modified: false },
    leisure: { amount: 0, modified: false },
    emergency: { amount: 0, modified: false },
    replacement: { amount: 0, modified: false },
    other: { amount: 0, description: '', modified: false },
  },
  stressTestCompleted: false,
  stressTestResults: null,
  quick: {
    yearsInRetirement: 0,
    yearlyExpense: 0,
  },
  stressTest: {
    initialCorpus: 0,
    yearlyExpense: 0,
    years: 30,
    equityAllocation: 50,
    debtAllocation: 40,
    goldAllocation: 10,
    expenseReductions: [
      { enabled: false, year: 10, percent: 15, reason: 'Kids independent' },
      { enabled: false, year: 20, percent: 10, reason: 'Simpler lifestyle' },
      { enabled: false, year: 25, percent: 10, reason: 'Reduced travel' },
    ],
  },
  // NEW: Investment Planner state
  investmentPlan: {
    currentAllocation: {
      equity: 50,
      debt: 40,
      gold: 10,
    },
    monthlyInvestment: 50000,
    yearlyIncreasePercent: 5,
    lumpsumAmount: 0,
  },
  // Single Source of Truth: Portfolio total from Portfolio Tracker
  portfolioTotal: 0,
  portfolioByCategory: {
    retirement: 0,
    fixed: 0,
    equity: 0,
    mf: 0,
    physical: 0,
    other: 0,
  },
};

const UserDataContext = createContext();

export const UserDataProvider = ({ children }) => {
  const [userData, setUserData] = useState(defaultUserData);

  // Deduplication for loadSavedPlanIntoContext
  const lastLoadedPlanId = useRef(null);
  const lastLoadTime = useRef(0);
  const LOAD_DEBOUNCE_MS = 500; // Minimum time between loads

  // Get the setResetUserDataCallback from AuthContext if available
  // We use a try-catch because UserDataProvider might be rendered before AuthProvider
  let setResetUserDataCallback = null;
  try {
    const auth = useAuth();
    setResetUserDataCallback = auth?.setResetUserDataCallback;
  } catch (e) {
    // AuthContext not available yet, which is fine
  }

  const updateComprehensive = useCallback((data) => {
    setUserData((prev) => ({
      ...prev,
      comprehensive: { ...prev.comprehensive, ...data },
    }));
  }, []);

  const updateExpenseAdjustments = useCallback((adjustments) => {
    setUserData((prev) => ({
      ...prev,
      expenseAdjustments: adjustments,
    }));
  }, []);

  const setFreedomNumber = useCallback((amount) => {
    setUserData((prev) => ({
      ...prev,
      calculatedFreedomNumber: amount,
    }));
  }, []);

  const updateGoal = useCallback((goalName, amount) => {
    setUserData((prev) => ({
      ...prev,
      goals: {
        ...prev.goals,
        [goalName]: {
          ...(typeof prev.goals[goalName] === 'object' ? prev.goals[goalName] : {}),
          amount: amount,
        },
      },
    }));
  }, []);

  const updateGoalEntries = useCallback((goalName, entries) => {
    setUserData((prev) => ({
      ...prev,
      goals: {
        ...prev.goals,
        [goalName]: {
          ...(typeof prev.goals[goalName] === 'object' ? prev.goals[goalName] : { amount: 0 }),
          entries,
        },
      },
    }));
  }, []);

  const markGoalModified = useCallback((goalName) => {
    setUserData((prev) => ({
      ...prev,
      goals: {
        ...prev.goals,
        [goalName]: {
          ...(typeof prev.goals[goalName] === 'object' ? prev.goals[goalName] : { amount: 0 }),
          modified: true,
        },
      },
    }));
  }, []);

  const updateOtherGoal = useCallback((amount, description) => {
    setUserData((prev) => ({
      ...prev,
      goals: {
        ...prev.goals,
        other: {
          ...(typeof prev.goals.other === 'object' ? prev.goals.other : {}),
          amount: amount,
          description: description,
        },
      },
    }));
  }, []);

  const setStressTestCompleted = useCallback((completed, results = null) => {
    setUserData((prev) => ({
      ...prev,
      stressTestCompleted: completed,
      stressTestResults: results,
    }));
  }, []);

  // NEW: Update investment plan state
  const updateInvestmentPlan = useCallback((data) => {
    setUserData((prev) => ({
      ...prev,
      investmentPlan: {
        ...prev.investmentPlan,
        ...data,
        // Handle nested currentAllocation update
        currentAllocation: data.currentAllocation
          ? { ...prev.investmentPlan.currentAllocation, ...data.currentAllocation }
          : prev.investmentPlan.currentAllocation,
      },
    }));
  }, []);

  // Single Source of Truth: Update portfolio total from Portfolio Tracker
  const updatePortfolioTotal = useCallback((total, byCategory = null) => {
    setUserData((prev) => ({
      ...prev,
      portfolioTotal: total,
      portfolioByCategory: byCategory || prev.portfolioByCategory,
      // Also update currentCorpus in comprehensive for consistency
      comprehensive: {
        ...prev.comprehensive,
        currentCorpus:
          prev.comprehensive.currentCorpus === 0 ? total : prev.comprehensive.currentCorpus,
      },
    }));
  }, []);

  // Helper to extract goal data from various formats
  const extractGoalData = (goalValue, defaultAmount = 0) => {
    if (goalValue === null || goalValue === undefined) {
      return { amount: defaultAmount, modified: false };
    }
    if (typeof goalValue === 'number') {
      return { amount: goalValue, modified: goalValue > 0 };
    }
    if (typeof goalValue === 'object') {
      return {
        amount: goalValue.amount || defaultAmount,
        modified: goalValue.modified === true || goalValue.amount > 0,
        ...(goalValue.description ? { description: goalValue.description } : {}),
      };
    }
    return { amount: defaultAmount, modified: false };
  };

  // Load saved plan into context - WITH DEDUPLICATION
  const loadSavedPlanIntoContext = useCallback((savedPlan) => {
    // Skip if same plan loaded recently
    const now = Date.now();
    const planId = savedPlan?.id;

    if (planId === lastLoadedPlanId.current && now - lastLoadTime.current < LOAD_DEBOUNCE_MS) {
      console.log('UserDataContext: Skipping duplicate load for plan', planId);
      return;
    }

    lastLoadedPlanId.current = planId;
    lastLoadTime.current = now;

    console.log('loadSavedPlanIntoContext called with:', savedPlan?.id);

    const compData = savedPlan.comprehensive_data || {};
    const savedGoals = compData.goals || {};
    const savedComprehensive = compData.comprehensive || {};
    const savedExpenseAdjustments = compData.expenseAdjustments;

    setUserData((prev) => ({
      ...prev,
      // Load comprehensive data
      comprehensive: {
        ...prev.comprehensive,
        ...savedComprehensive,
      },
      // Load expense adjustments if available
      expenseAdjustments:
        savedExpenseAdjustments &&
        Array.isArray(savedExpenseAdjustments) &&
        savedExpenseAdjustments.length > 0
          ? savedExpenseAdjustments
          : prev.expenseAdjustments,
      // Load freedom number
      calculatedFreedomNumber: compData.freedomNumber || 0,
      // Load goals with proper structure
      goals: {
        education: extractGoalData(savedGoals.education),
        marriage: extractGoalData(savedGoals.marriage),
        loanClosure: extractGoalData(savedGoals.loanClosure),
        leisure: extractGoalData(savedGoals.leisure),
        emergency: extractGoalData(savedGoals.emergency),
        replacement: extractGoalData(savedGoals.replacement),
        other: {
          ...extractGoalData(savedGoals.other),
          description: savedGoals.other?.description || compData.otherDescription || '',
        },
      },
      // Load stress test data
      stressTest: {
        initialCorpus: savedPlan.initial_corpus || prev.stressTest.initialCorpus,
        yearlyExpense: savedPlan.yearly_expense || prev.stressTest.yearlyExpense,
        years: savedPlan.years || prev.stressTest.years,
        equityAllocation: savedPlan.equity_allocation || prev.stressTest.equityAllocation,
        debtAllocation: savedPlan.debt_allocation || prev.stressTest.debtAllocation,
        goldAllocation: savedPlan.gold_allocation || prev.stressTest.goldAllocation,
        expenseReductions: savedPlan.expense_reductions || prev.stressTest.expenseReductions,
      },
      // Load investment plan data
      investmentPlan: {
        currentAllocation:
          compData.investmentPlan?.currentAllocation || prev.investmentPlan.currentAllocation,
        monthlyInvestment:
          compData.investmentPlan?.monthlyInvestment || prev.investmentPlan.monthlyInvestment,
        yearlyIncreasePercent:
          compData.investmentPlan?.yearlyIncreasePercent ||
          prev.investmentPlan.yearlyIncreasePercent,
        lumpsumAmount: compData.investmentPlan?.lumpsumAmount || prev.investmentPlan.lumpsumAmount,
      },
      stressTestCompleted: compData.stressTestCompleted || false,
      stressTestResults: compData.stressTestResults || null,
    }));

    console.log('Plan loaded into context successfully');
  }, []);

  const getDataForSave = useCallback(() => {
    return { ...userData };
  }, [userData]);

  const hasUserData = useCallback(() => {
    const comp = userData.comprehensive;
    return comp.currentAge > 0 || comp.monthlyExpenses > 0 || comp.currentCorpus > 0;
  }, [userData]);

  const updateQuick = useCallback((data) => {
    setUserData((prev) => ({
      ...prev,
      quick: { ...prev.quick, ...data },
    }));
  }, []);

  const updateStressTest = useCallback((data) => {
    setUserData((prev) => ({
      ...prev,
      stressTest: { ...prev.stressTest, ...data },
    }));
  }, []);

  // FIXED: Reset all data to defaults (called on sign out)
  const resetAll = useCallback(() => {
    console.log('Resetting all user data to defaults');
    setUserData(defaultUserData);
  }, []);

  // FIXED: Register resetAll with AuthContext so it can be called on sign out
  useEffect(() => {
    if (setResetUserDataCallback) {
      setResetUserDataCallback(() => resetAll);
    }
  }, [setResetUserDataCallback, resetAll]);

  return (
    <UserDataContext.Provider
      value={{
        userData,
        updateComprehensive,
        updateExpenseAdjustments,
        setFreedomNumber,
        updateGoal,
        updateGoalEntries,
        markGoalModified,
        updateOtherGoal,
        updateQuick,
        updateStressTest,
        updateInvestmentPlan,
        updatePortfolioTotal,
        setStressTestCompleted,
        loadSavedPlanIntoContext,
        getDataForSave,
        hasUserData,
        resetAll,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within UserDataProvider');
  }
  return context;
};

export default UserDataContext;
